import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import chartColorPalette from 'assets/styles/chartColorPalette';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { Survey, SurveyStatsResponse } from 'services/types/survey';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import SurveysEvolutionChart from './SurveysEvolutionChart';
import SurveysMetricsCards from './SurveysMetricsCards';

interface SurveysDashboardProps {
  surveys: Survey[];
  isLoading: boolean;
  surveyStats?: SurveyStatsResponse;
  isLoadingStats?: boolean;
}

interface SurveyMetrics {
  surveyName: string;
  totalSurveys: number;
  pendingCount: number;
  finishedCount: number;
  abortedCount: number;
  totalQuestionsAnswered: number;
  questions: QuestionMetrics[];
}

interface QuestionMetrics {
  question: string;
  responses: ResponseCount[];
  labelAngle: number;
}

interface ResponseCount {
  response: string;
  count: number;
}

/**
 * Checks if a question is a comment/question type
 * @param question The question text to check
 * @returns True if the question contains variations of "comentario" or "comment"
 */
const isCommentQuestion = (question: string): boolean => {
  const normalizedQuestion = question.toLowerCase().trim();
  const commentKeywords = ['comentario', 'comentarios', 'comment', 'comments', 'observacion', 'observaciones'];
  return commentKeywords.some((keyword) => normalizedQuestion.includes(keyword));
};

/**
 * Groups surveys by name and calculates metrics for each survey type
 * @param surveys Array of surveys to analyze
 * @returns Array of survey metrics grouped by survey name
 */
const calculateSurveyMetrics = (surveys: Survey[]): SurveyMetrics[] => {
  if (!surveys || surveys.length === 0) return [];

  // Group surveys by name
  const surveyGroups = surveys.reduce(
    (acc, survey) => {
      if (!acc[survey.name]) {
        acc[survey.name] = [];
      }
      acc[survey.name].push(survey);
      return acc;
    },
    {} as Record<string, Survey[]>
  );

  // Calculate metrics for each survey group
  const surveyMetrics = Object.entries(surveyGroups).map(([surveyName, surveysOfType]) => {
    const totalSurveys = surveysOfType.length;
    const pendingCount = surveysOfType.filter((s) => s.status === 'pending').length;
    const finishedCount = surveysOfType.filter((s) => s.status === 'finished').length;
    const abortedCount = surveysOfType.filter((s) => s.status === 'aborted').length;

    // Calculate total questions answered
    const totalQuestionsAnswered = surveysOfType.reduce((total, survey) => {
      if (!survey.results || !Array.isArray(survey.results)) return total;
      return total + survey.results.filter((result) => result.response && result.response.trim() !== '').length;
    }, 0);

    // Collect all questions and track which surveys have each question
    const questionData: Record<
      string,
      {
        responses: string[];
        surveysWithQuestion: Survey[];
      }
    > = {};

    surveysOfType.forEach((survey) => {
      if (!survey.results || !Array.isArray(survey.results)) return;

      survey.results.forEach((result) => {
        if (!questionData[result.question]) {
          questionData[result.question] = {
            responses: [],
            surveysWithQuestion: [],
          };
        }

        // Track all surveys that have this question
        if (!questionData[result.question].surveysWithQuestion.includes(survey)) {
          questionData[result.question].surveysWithQuestion.push(survey);
        }

        // Track responses (only non-empty ones)
        if (result.response && result.response.trim() !== '') {
          questionData[result.question].responses.push(result.response);
        }
      });
    });

    // Calculate response counts for each question
    const questions: QuestionMetrics[] = Object.entries(questionData).map(([question, data]) => {
      const { responses, surveysWithQuestion } = data;

      // Special handling for comment questions
      if (isCommentQuestion(question)) {
        const respondedCount = responses.length;
        const notRespondedCount = surveysWithQuestion.length - respondedCount;

        const responseCountArray: ResponseCount[] = [];
        if (respondedCount > 0) {
          responseCountArray.push({
            response: 'Respondió',
            count: respondedCount,
          });
        }
        if (notRespondedCount > 0) {
          responseCountArray.push({
            response: 'No respondió',
            count: notRespondedCount,
          });
        }

        return {
          question,
          responses: responseCountArray,
          labelAngle: 0,
        };
      }

      // Regular question processing
      const responseCounts: Record<string, number> = {};

      // Helper function to normalize "yes" and "no" variants
      const normalizeResponse = (response: string): string => {
        const normalized = response.toLowerCase().trim();

        // Normalize "yes" variants
        if (['si', 'sí', 'y', 'yes'].includes(normalized)) {
          return 'Sí'; // Standardize all yes variants to "Sí"
        }

        // Normalize "no" variants
        if (['no', 'n'].includes(normalized)) {
          return 'No'; // Standardize all no variants to "No"
        }

        return response;
      };

      responses.forEach((response) => {
        const normalizedResponse = normalizeResponse(response);
        responseCounts[normalizedResponse] = (responseCounts[normalizedResponse] || 0) + 1;
      });

      // Helper function to check if all responses are numeric
      const allResponsesAreNumeric = (responsesList: string[]): boolean => {
        return responsesList.every((response) => !Number.isNaN(Number(response.trim())));
      };

      // Helper function to wrap long responses with line breaks
      const wrapResponse = (response: string): string => {
        const maxLineLength = 25; // Characters per line
        const maxTotalLength = 50; // Maximum total characters

        // First truncate if too long
        const processedResponse =
          response.length > maxTotalLength ? `${response.substring(0, maxTotalLength - 3)}...` : response;

        // Then add line breaks for readability
        if (processedResponse.length <= maxLineLength) {
          return processedResponse;
        }

        const words = processedResponse.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        words.forEach((word) => {
          if (currentLine.length + word.length + 1 <= maxLineLength) {
            currentLine = currentLine ? `${currentLine} ${word}` : word;
          } else if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            // Word is too long, break it
            lines.push(word.substring(0, maxLineLength));
            currentLine = word.substring(maxLineLength);
          }
        });

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines.join('\n');
      };

      // Check if all responses are short (5 characters or less)
      const allResponsesAreShort = Object.keys(responseCounts).every((response) => response.length <= 5);
      const labelAngle = allResponsesAreShort ? 0 : -45;

      // Get unique responses for sorting logic
      const uniqueResponses = Object.keys(responseCounts);
      const areAllNumeric = allResponsesAreNumeric(uniqueResponses);

      let responseCountArray: ResponseCount[] = Object.entries(responseCounts).map(([response, count]) => ({
        response: wrapResponse(response),
        count,
      }));

      // Sort responses based on the rules
      responseCountArray.sort((a, b) => {
        // First priority: "Yes" responses come first, then "No" responses
        const aIsYes = a.response === 'Sí';
        const bIsYes = b.response === 'Sí';
        const aIsNo = a.response === 'No';
        const bIsNo = b.response === 'No';

        // Sí comes first
        if (aIsYes && !bIsYes) return -1;
        if (!aIsYes && bIsYes) return 1;

        // No comes second (after Sí but before everything else)
        if (aIsNo && !bIsNo && !bIsYes) return -1;
        if (!aIsNo && bIsNo && !aIsYes) return 1;

        // Second priority: if all responses are numeric, sort by numeric value
        if (areAllNumeric) {
          const aNum = Number(a.response.replace('...', ''));
          const bNum = Number(b.response.replace('...', ''));
          return aNum - bNum;
        }

        // Third priority: sort by count (descending)
        return b.count - a.count;
      });

      // Group long tail responses into "Otros" if there are more than 20 responses
      if (responseCountArray.length > 20) {
        const topResponses = responseCountArray.slice(0, 20);
        const tailResponses = responseCountArray.slice(20);

        // Calculate total count for "Otros"
        const otrosCount = tailResponses.reduce((sum, item) => sum + item.count, 0);

        // Add "Otros" category
        const otrosResponse: ResponseCount = {
          response: 'Otros',
          count: otrosCount,
        };

        responseCountArray = [...topResponses, otrosResponse];
      }

      return {
        question,
        responses: responseCountArray,
        labelAngle,
      };
    });

    return {
      surveyName,
      totalSurveys,
      pendingCount,
      finishedCount,
      abortedCount,
      totalQuestionsAnswered,
      questions,
    };
  });

  // Sort surveys by total count (descending) - surveys with most responses first
  return surveyMetrics.sort((a, b) => b.totalSurveys - a.totalSurveys);
};

/**
 * Component to display metrics for a single survey type
 */
const SurveyMetricsCard: React.FC<{ metrics: SurveyMetrics }> = ({ metrics }) => (
  <Box
    sx={{
      mt: 2,
      mb: 4,
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_COIN,
      boxShadow: SHADOW,
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0px 16px 40px rgba(21, 24, 58, 0.1)',
      },
    }}
  >
    <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 700,
          fontSize: '1.15rem',
          color: NAVY,
          mb: 2,
        }}
      >
        {metrics.surveyName}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Encuestas', value: metrics.totalSurveys },
          { label: 'Finalizadas', value: metrics.finishedCount },
          { label: 'Preguntas Respondidas', value: metrics.totalQuestionsAnswered },
        ].map((item) => (
          <Grid size={{ xs: 4, sm: 4 }} key={item.label}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: NAVY,
                  lineHeight: 1.1,
                }}
              >
                {item.value}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: `${NAVY}8C`,
                  mt: 0.5,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {metrics.questions.length > 0 &&
        metrics.questions.map((question) => (
          <Box key={`${metrics.surveyName}-${question.question}`} sx={{ mt: 3 }}>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: NAVY,
                textAlign: 'center',
                mt: 2,
              }}
            >
              {question.question}
            </Typography>

            {question.responses.length > 0 &&
              (question.responses.length === 1 ? (
                <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: NAVY,
                      mb: 0.5,
                    }}
                  >
                    Respuesta única:{' '}
                    <Typography component="span" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>
                      {question.responses[0].response}
                    </Typography>
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.85rem',
                      color: `${NAVY}80`,
                    }}
                  >
                    ({question.responses[0].count} respuesta{question.responses[0].count !== 1 ? 's' : ''})
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: 400, width: '100%' }}>
                  <BarChart
                    dataset={question.responses.map((item, idx) => ({
                      ...item,
                      id: idx,
                    }))}
                    colors={chartColorPalette}
                    xAxis={[
                      {
                        scaleType: 'band',
                        dataKey: 'response',
                        height: 'auto' as const,
                        tickLabelStyle: {
                          angle: question.labelAngle,
                          textAnchor: question.labelAngle === 0 ? 'middle' : 'end',
                        },
                      },
                    ]}
                    series={[
                      {
                        dataKey: 'count',
                      },
                    ]}
                    yAxis={[
                      {
                        min: 0,
                        tickMinStep: 1,
                      },
                    ]}
                    margin={{
                      left: 50,
                      right: 50,
                      top: 50,
                      bottom: 150,
                    }}
                  />
                </Box>
              ))}
          </Box>
        ))}
    </Box>
  </Box>
);

const SurveysDashboard: React.FC<SurveysDashboardProps> = ({ surveys, isLoading, surveyStats, isLoadingStats }) => {
  const [activeTab, setActiveTab] = useState(0);
  const surveyMetrics = useMemo(() => calculateSurveyMetrics(surveys), [surveys]);
  const statsLoading = isLoadingStats ?? false;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  return (
    <Box>
      <ToggleButtonGroup
        value={activeTab}
        exclusive
        onChange={(_, v) => {
          if (v !== null) setActiveTab(v);
        }}
        size="small"
        sx={{
          mb: 3,
          '& .MuiToggleButton-root': {
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '0.8rem',
            textTransform: 'none',
            borderRadius: RADIUS_ITEM,
            border: 'none',
            color: `${NAVY}99`,
            backgroundColor: SURFACE_CONTAINER_LOW,
            px: 2,
            py: 0.75,
            transition: 'all 0.2s ease-in-out',
            '&.Mui-selected': {
              color: NAVY,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              boxShadow: '0px 2px 8px rgba(21, 24, 58, 0.08)',
            },
            '&:hover': {
              backgroundColor: `${NAVY}0A`,
            },
          },
          '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
            borderLeft: 'none',
            ml: 0.5,
          },
          '& .MuiToggleButtonGroup-grouped:first-of-type': {
            borderRadius: RADIUS_ITEM,
          },
          '& .MuiToggleButtonGroup-grouped:last-of-type': {
            borderRadius: RADIUS_ITEM,
          },
        }}
      >
        <ToggleButton value={0}>Resumen</ToggleButton>
        <ToggleButton value={1}>Análisis</ToggleButton>
      </ToggleButtonGroup>
      {activeTab === 0 && (
        <>
          <SurveysMetricsCards metrics={surveyStats?.hardMetrics} isLoading={statsLoading} />
          <SurveysEvolutionChart data={surveyStats?.chartData.byWeek} isLoading={statsLoading} />
        </>
      )}
      {activeTab === 1 && surveyMetrics.length > 0 && (
        <>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '1.1rem',
              color: NAVY,
              mb: 2,
            }}
          >
            Detalle por Pregunta
          </Typography>
          {surveyMetrics.map((metrics) => (
            <SurveyMetricsCard key={metrics.surveyName} metrics={metrics} />
          ))}
        </>
      )}
      {activeTab === 1 && surveyMetrics.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              color: `${NAVY}80`,
            }}
          >
            No hay datos de análisis disponibles
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SurveysDashboard;
